import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import { AuthEndpoint } from './Auth.api';
import { AuthReducer } from './Auth.reducer';
import { BaseReducer } from '../../../../store/BaseReducer';
import { AUTH_LOCAL_STORAGE_KEY } from './constants';

// The Authorization header of the request, or null when none was sent.
const sentAuthorizationOf = (action: any): string | null =>
  action.meta?.baseQueryMeta?.request?.headers?.get?.('Authorization') ?? null;

// True when the request was sent with the current session's token, or when it
// was sent without a token and there is still none. The service sends the
// token as "Bearer <token>" and sends no header without one (service.ts).
const sentForCurrentSession = (action: any) => {
  const token = localStorage.getItem(AUTH_LOCAL_STORAGE_KEY);
  const sentAuthorization = sentAuthorizationOf(action);
  return token ? sentAuthorization === `Bearer ${token}` : sentAuthorization === null;
};

const listenerMiddleware = createListenerMiddleware();

listenerMiddleware.startListening({
  matcher: isAnyOf(AuthEndpoint.endpoints.getMe.matchFulfilled, AuthEndpoint.endpoints.getMe.matchRejected),
  effect: (action: any, listenerApi) => {
    // A result for a previous session must not replace the current user or end
    // the current session.
    if (!sentForCurrentSession(action)) {
      return;
    }
    // Only 401 and 403 end the session. A network error or a 5xx keeps it, and
    // the app shows a retry screen.
    if (action.payload && (action.payload.status === 401 || action.payload.status === 403)) {
      listenerApi.dispatch(AuthReducer.actions.setUnauthorizedAccess({}));
    } else if (action.payload && action.meta?.baseQueryMeta.response?.status === 200) {
      listenerApi.dispatch(AuthReducer.actions.setAuthorizedAccess({ value: action.payload }));
      listenerApi.dispatch(AuthReducer.actions.setAccountLoadFailed({ value: false }));
    } else if (AuthEndpoint.endpoints.getMe.matchRejected(action) && sentAuthorizationOf(action) !== null) {
      // The retry screen is for an existing session. A request sent without a
      // token has no session to recover, so the sign-in pages stay available.
      listenerApi.dispatch(AuthReducer.actions.setAccountLoadFailed({ value: true }));
    }
  },
});
listenerMiddleware.startListening({
  matcher: isAnyOf(AuthEndpoint.endpoints.switchRole.matchFulfilled, AuthEndpoint.endpoints.switchRole.matchRejected),
  effect: (action: any, listenerApi) => {
    // A switch that a previous session started must not change the modules of
    // the current session.
    if (action.payload && action.meta?.baseQueryMeta.response?.status === 200 && sentForCurrentSession(action)) {
      listenerApi.dispatch(BaseReducer.actions.setModules({ value: action.payload }));
    }
  },
});
listenerMiddleware.startListening({
  matcher: isAnyOf(AuthEndpoint.endpoints.getSignUpConfig.matchFulfilled),
  effect: (action: any, listenerApi) => {
    if (action.payload && action.meta?.baseQueryMeta.response?.status === 200) {
      listenerApi.dispatch(AuthReducer.actions.prepareForm({ value: action.payload }));
    }
  },
});

export const authMiddleware = listenerMiddleware.middleware;
